import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { ICountry } from '@interfaces/models/seller-info/seller-information';
import { SellerInformationContext } from '@context/seller-information.context';
import { useContextSelector } from 'use-context-selector';
import Icon from '@components/atoms/icon/icon';
import sanitizer from '@helpers/sanitizer';
import { accentFold } from '@helpers/utils/general';
import styles from './countries-multi-select.module.scss';

type CountriesMultiSelectProps = {
  countries: ICountry[];
  preSelectedCountries?: string[];
  onSelectedCountries: (selectedCountries: ICountry[]) => void;
};

const CountriesMultiSelect: React.FC<CountriesMultiSelectProps> = (props) => {
  const { preSelectedCountries = [], onSelectedCountries } = props;
  const { t } = useTranslation('get-paid');
  const countries = useContextSelector(SellerInformationContext, (value) => value.countries);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [filterText, setFilterText] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectRef = useRef<HTMLDivElement | null>(null);

  // Ensure that selectedCountries reflect the initial state
  useEffect(() => {
    if (!preSelectedCountries.length) {
      return;
    }

    setSelectedCountries(preSelectedCountries?.map((country) => country));
  }, [preSelectedCountries]);

  useEffect(() => {
    const selectedCountriesa = selectedCountries.map((code) => countries.find((country) => country.code === code));
    onSelectedCountries(selectedCountriesa);
  }, [selectedCountries]);

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsOpen(!isOpen);
    if (isOpen) {
      inputRef.current?.focus();
    }
  };

  const toggleCountry = (code: string) => {
    if (selectedCountries.includes(code)) {
      setSelectedCountries(selectedCountries.filter((selectedCode) => selectedCode !== code));
    } else {
      setSelectedCountries([...selectedCountries, code]);
    }

    // Focus on the input unless dropdown gets closed
    inputRef?.current?.focus();
  };

  const filteredCountries = countries.filter((country) =>
    accentFold(country.name.toLowerCase()).includes(accentFold(filterText.toLowerCase())),
  );

  const updateFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(e.target.value);
    setIsOpen(true);
  };

  const clearFilter = () => {
    setFilterText('');
    setIsOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen) {
        const target = event.target as Element;
        if (!target.closest(`.${styles['multi-select']}`)) {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      inputRef?.current?.focus();
    }
  }, [isOpen]);

  const formatSelectedCountries = () => {
    return selectedCountries.map((code) => countries.find((country) => country.code === code)?.name).join(', ');
  };

  const formatLabel = (label, searchText) => {
    const regex = new RegExp(searchText, 'gi');
    return label.replace(regex, (match) => `<strong>${match}</strong>`);
  };

  return (
    <div
      className={styles['multi-select']}
      ref={selectRef}
    >
      <div
        onClick={toggleDropdown}
        role="button"
        onKeyDown={(e) => toggleDropdown(e)}
        className={styles['select-container']}
        tabIndex={0}
      >
        <div className={styles['selected-countries']}>
          {selectedCountries.length > 0 ? (
            <p className={clsx(styles['format-selected'])}>
              <span className={styles['ellipsis-text']}>{formatSelectedCountries()}</span>
            </p>
          ) : (
            <button
              className={styles['toggle-select']}
              onClick={(e) => toggleDropdown(e)}
            >
              {t('GET_PAID.TAX_INFORMATION_FORM.EXTRA_COUNTRY_SELECT', 'Select countries')}
            </button>
          )}
        </div>
      </div>
      {isOpen && (
        <div className={styles['options-container']}>
          <div className={styles['input-container']}>
            <Icon
              name="search-default"
              className={styles['input-search-icon']}
              ariaHidden
            />
            <input
              type="text"
              placeholder={t('GET_PAID.TAX_INFORMATION_FORM.EXTRA_COUNTRY_SEARCH', 'Search for a country')}
              value={filterText}
              onChange={updateFilter}
              className={styles.optionsInput}
              onClick={(e) => e.stopPropagation()}
              ref={inputRef}
              autoComplete="off"
            />
            {filterText && (
              <button
                data-cy="countries-multi-select-clear-button"
                className={styles['close-button']}
                type="button"
                onClick={clearFilter}
                aria-label={t('MODAL.BUTTON.CLOSE')}
              >
                <Icon
                  name="close-default"
                  className={styles['close-button']}
                  ariaHidden
                />
                <span className="visuallyhidden">{t('MODAL.BUTTON.CLOSE')}</span>
              </button>
            )}
          </div>
          <ul className={styles['options-list']}>
            {filteredCountries.length === 0 ? (
              <p className={styles['options-list-empty']}>
                {t('GET_PAID.TAX_INFORMATION_FORM.EXTRA_COUNTRY_NOTFOUND', 'No countries found!')}
              </p>
            ) : (
              filteredCountries.map((country) => (
                <li key={country.code}>
                  <input
                    id={country.code}
                    type="checkbox"
                    className={clsx('vc-checkbox__input', styles['vc-checkbox__input'])}
                    checked={selectedCountries.includes(country.code)}
                    onChange={() => {
                      toggleCountry(country.code);
                    }}
                  />
                  <label
                    htmlFor={country.code}
                    className={clsx('vc-checkbox__label', styles['vc-checkbox__label'], {
                      'vc-text-bold': selectedCountries.includes(country.code),
                    })}
                    dangerouslySetInnerHTML={{
                      __html: sanitizer(formatLabel(country.name, filterText)),
                    }}
                  />
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CountriesMultiSelect;
